import type { OtherPaymentLine } from 'components/OtherPayments'
import { removeComma } from 'utils'

/**
 * Which of a payoff's "pay others" lines are the borrower being handed back the payoff's OWN
 * overage, and so are not money the payoff collects.
 *
 * A payoff that takes in more than it needs holds the difference in Suspense, and the servicer
 * hands it back by adding a pay-other line to the borrower on the same payment. Those two halves
 * are one movement - money into Suspense and straight back out of it - which is why the server
 * posts that payout against the suspense bucket (see the backend's `withBorrowerRefundBucket`).
 *
 * The screen read the line the other way round: every pay-other line is money collected ON TOP of
 * the loan's own obligations, which is right for a pass-through fee the borrower is being charged
 * and wrong for their own overage coming back. So handing back a $673.03 overage added $673.03 to
 * Amount Due and to Amount Paid, and deducted it a second time from the funds left to allocate.
 * On longhorn 53961 (TMO 313087, Smartsheet row 505) that left Amount Paid reading $318,636.95
 * against a $317,963.92 wire - "this should not change the amount received".
 *
 * The predicate is deliberately the same two signals the backend already uses to tell an overage
 * apart from a fee on the very same kind of line: if the two halves disagreed about which lines
 * are a refund, the screen would allocate against one set and the ledger post against the other.
 */

/** `PayablePayeeType.Borrower`, as the line stores it. */
const BORROWER_PAYEE_TYPE = 'Borrower'

/** The backend's `isBorrowerRefundMemo`, kept word for word - TMO spells the word several ways. */
const isBorrowerRefundMemo = (memo?: string): boolean =>
  /overag|overpay/.test(
    String(memo ?? '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase(),
  )

const num = (value: any): number => Number(removeComma(value ?? 0) || 0)

/**
 * Whether this line is money going back to the borrower.
 *
 * Either signal is enough. `payeeType` is what the payee lookup sets when the servicer picks the
 * borrower and is the reliable one; the memo test catches a refund keyed against a free-text payee.
 */
export const isBorrowerRefundLine = (line?: OtherPaymentLine | null): boolean =>
  String(line?.payeeType || '').trim() === BORROWER_PAYEE_TYPE || isBorrowerRefundMemo(line?.memo)

/**
 * What the pay-other lines COLLECT: the total, less the borrower refunds on a payoff.
 *
 * Only on a payoff, because only a payoff holds an overage to hand back. On every other payment
 * type a line paid to the borrower is money being collected and passed through like any other, and
 * this is the plain total it has always been.
 */
export const otherPaymentsCollected = (rows: OtherPaymentLine[] = [], isPayoff: boolean): number => {
  const counted = isPayoff ? (rows || []).filter((line) => !isBorrowerRefundLine(line)) : rows || []
  return Number(counted.reduce((sum, line) => sum + num(line?.amount), 0).toFixed(2))
}

/** The memo the overage refund is keyed with, which is what longhorn's own queue already reads. */
export const BORROWER_OVERAGE_MEMO = 'OVERAGE'

/** What the borrower-refund lines come to. The backend's `borrowerRefundTotal`, same predicate. */
export const borrowerRefundTotal = (rows: OtherPaymentLine[] = []): number =>
  Number(
    (rows || [])
      .filter((line) => isBorrowerRefundLine(line))
      .reduce((sum, line) => sum + num(line?.amount), 0)
      .toFixed(2),
  )

/** The suspense bucket read forward through this payment and the refund it raises. */
export interface SuspenseAfterRefund {
  /** Suspense the loan already holds, before this payment. */
  opening: number
  /** What this payment puts in: the overage it collected, plus anything held by hand. */
  credited: number
  /** Suspense drawn back out to fund this payment: Source of Funds > From Suspense. */
  applied: number
  /** What the borrower-refund lines on this payment will take out of the bucket. */
  refunded: number
  /** What the bucket reads once this payment is applied and the refund check is paid. */
  closing: number
  /**
   * Refund beyond what THIS payment credited to suspense, which is the figure that matters rather
   * than the closing balance: the backend routes the payout to suspense all-or-nothing against the
   * payment's own credit (`withBorrowerRefundBucket`), so a cent over and the whole refund posts
   * against the Construction Reserve instead and the overage stays in suspense for ever. That is
   * Smartsheet row 439, reached this time by a keying error rather than a missing default.
   */
  overRefunded: number
}

/**
 * Where suspense lands once the overage is handed back: the figure a servicer keying the refund
 * has no way to work out from the screen (Smartsheet row 548, lbetty).
 *
 * The overage goes into suspense and the refund comes straight back out of it, but the two halves
 * are entered in different places: the amount in the Suspense field at the top, the payee and the
 * amount at the bottom. Nothing tied them together, so a mistyped refund was invisible until the
 * ledger disagreed.
 */
export const suspenseAfterRefund = ({
  opening = 0,
  credited = 0,
  applied = 0,
  rows = [],
}: {
  opening?: number | string
  credited?: number | string
  applied?: number | string
  rows?: OtherPaymentLine[]
}): SuspenseAfterRefund => {
  const open = num(opening)
  const credit = num(credited)
  const draw = num(applied)
  const refunded = borrowerRefundTotal(rows)
  return {
    opening: open,
    credited: credit,
    applied: draw,
    refunded,
    closing: Number((open + credit - draw - refunded).toFixed(2)),
    overRefunded: Number(Math.max(0, refunded - credit).toFixed(2)),
  }
}
